import React, { createContext, useContext, ReactNode } from 'react';
import { useAppSelector } from '@/hooks/useAppSelector';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { setSelectedVersion as setReduxSelectedVersion } from '@/store/versionsSlice';

interface VersionContextType {
  selectedVersion: string | null;
  setSelectedVersion: (version: string | null) => void;
}

const VersionContext = createContext<VersionContextType | undefined>(undefined);

/**
 * VersionProvider - A context provider that uses Redux for version state management
 * This simplifies the implementation by directly using the Redux state instead of
 * maintaining a separate local state that needs to be synced
 */
export const VersionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const dispatch = useAppDispatch();
  const selectedVersion = useAppSelector(state => state.versions.selectedVersion);

  // Setter function that updates the Redux state
  const setSelectedVersion = (version: string | null) => {
    dispatch(setReduxSelectedVersion(version));
  };

  return (
    <VersionContext.Provider value={{ 
      selectedVersion, 
      setSelectedVersion
    }}>
      {children}
    </VersionContext.Provider>
  );
};

export const useVersion = (): VersionContextType => {
  const context = useContext(VersionContext);
  if (context === undefined) {
    throw new Error('useVersion must be used within a VersionProvider');
  }
  
  return context;
};
